import React, { useCallback, useMemo } from 'react';
import { styled } from 'styled-components';

import { Box } from '@rover/kibble/core';
import useLocationContext from '@rover/react-lib/src/context/useLocationContext';
import { TrainerSearchResult } from '@rover/rsdk/src/apiClient/latest/model/trainerSearchResult';
import { TrainingSearchQuery } from '@rover/rsdk/src/apiClient/latest/model/trainingSearchQuery';

import { RoverLoading } from '../../../../components';
import {
  CURRENT_BEHAVIOR_SLUGS_TO_LABELS,
  CURRENT_SKILL_SLUGS_TO_LABELS,
} from '../../components/TrainingNeedsPills/constants';
import { SkillsAndBehaviorSlugsDictionary } from '../../utils/params';
import { SearchResultSectionLayout } from '../layouts';
import { getQueryStringForTrainerSearchCards } from '../params';
import { GroupedResults } from '../utilities';

import MarketingContextHeader from './MarketingContextHeader';
import NoResults from './NoResults';
import ResultsInfoBanner from './ResultsInfoBanner';
import SearchResultGrouping, { SearchResultGroupingVariant } from './SearchResultGrouping';
import TrainerSearchCard from './TrainerSearchCard';

interface SearchResultSectionProps {
  isMapExpanded?: boolean;
  results: TrainerSearchResult[];
  resultsLoading: boolean;
  shouldUsePriceTransparency?: boolean;
  groupedResults: GroupedResults;
  selectedSkillsAndBehaviors: SkillsAndBehaviorSlugsDictionary;
  searchQuery: TrainingSearchQuery;
  onMarketingContextHeaderLearnMore: () => void;
  onFaqPreviewBannerReadMore: () => void;
  shouldDisplayMarketingContextHeader: boolean;
}

interface StyledRoverLoadingProps {
  centerContent?: boolean;
}
const StyledRoverLoading = styled(RoverLoading)<StyledRoverLoadingProps>`
  display: flex;
  flex-direction: column;
  justify-content: ${({ centerContent }) => (centerContent ? 'center' : 'flex-start')};
  flex-grow: 1;

  &:after {
    top: 40px !important;
  }
`;

export default function SearchResultSection({
  isMapExpanded = false,
  results,
  resultsLoading,
  shouldUsePriceTransparency = false,
  groupedResults,
  selectedSkillsAndBehaviors,
  searchQuery,
  onMarketingContextHeaderLearnMore,
  onFaqPreviewBannerReadMore,
  shouldDisplayMarketingContextHeader,
}: SearchResultSectionProps): JSX.Element {
  const showNoResultsMessage = results.length === 0;

  const { search: initialQueryParams } = useLocationContext();

  const queryStringForCards = useMemo(
    () =>
      getQueryStringForTrainerSearchCards({
        searchPageQueryString: initialQueryParams,
        selectedSkillsAndBehaviors,
        // Subtract 1 to omit the trainer whose profile is being viewed
        nearbyTrainerCount: results.filter((result) => !result.virtualOnly).length - 1,
        searchQuery,
      }),
    [selectedSkillsAndBehaviors, initialQueryParams, results, searchQuery]
  );

  const mapToCards = useCallback(
    (resultsToMap: TrainerSearchResult[]): JSX.Element[] => {
      return resultsToMap.map((result, i) => {
        const href = queryStringForCards
          ? `${result.trainerWebUrl}?${queryStringForCards}`
          : result.trainerWebUrl;

        return (
          <TrainerSearchCard
            key={result.personOpk}
            index={i}
            searchResult={result}
            href={href}
            shouldUsePriceTransparency={shouldUsePriceTransparency}
          />
        );
      });
    },
    [queryStringForCards, shouldUsePriceTransparency]
  );

  const trainerCards = useMemo(() => {
    if (
      groupedResults.fullMatchResults.length > 0 ||
      groupedResults.partialMatchResults.length > 0
    ) {
      const otherMatchCards = mapToCards(groupedResults.otherResults);

      if (groupedResults.fullMatchResults.length > 0) {
        const behaviors = selectedSkillsAndBehaviors.behaviorsSlugs.map(
          (behavior) => CURRENT_BEHAVIOR_SLUGS_TO_LABELS[behavior]
        );
        const skills = selectedSkillsAndBehaviors.skillsSlugs.map(
          (skill) => CURRENT_SKILL_SLUGS_TO_LABELS[skill]
        );
        const needs = behaviors.concat(skills);
        const fullMatchCards = mapToCards(groupedResults.fullMatchResults);
        const fullMatchGroup = (
          <SearchResultGroupingVariant variant="fullMatch" needs={needs}>
            {fullMatchCards}
          </SearchResultGroupingVariant>
        );

        const otherMatchGroup = (
          <SearchResultGroupingVariant variant="otherMatchIncludingPartialMatches">
            {otherMatchCards}
          </SearchResultGroupingVariant>
        );

        return [fullMatchGroup, otherMatchGroup];
      }

      const partialMatchCards = mapToCards(groupedResults.partialMatchResults);
      const partialMatchGroup = (
        <SearchResultGroupingVariant variant="partialMatch">
          {partialMatchCards}
        </SearchResultGroupingVariant>
      );

      const otherMatchGroup = (
        <SearchResultGroupingVariant variant="otherMatch">
          {otherMatchCards}
        </SearchResultGroupingVariant>
      );

      return [partialMatchGroup, otherMatchGroup];
    }

    if (groupedResults.otherResults.length > 0) {
      const noMatchCards = mapToCards(groupedResults.otherResults);
      const noMatchGroup = (
        <SearchResultGroupingVariant variant="noMatch">{noMatchCards}</SearchResultGroupingVariant>
      );
      return noMatchGroup;
    }

    const resultsCards = mapToCards(results);
    return <SearchResultGrouping>{resultsCards}</SearchResultGrouping>;
  }, [results, groupedResults, mapToCards, selectedSkillsAndBehaviors]);

  return (
    <SearchResultSectionLayout isMapExpanded={isMapExpanded}>
      {shouldDisplayMarketingContextHeader && (
        <Box data-testid="desktop-marketing-context-header" display={['none', 'block']}>
          <MarketingContextHeader onLearnMore={onMarketingContextHeaderLearnMore} isDesktop />
        </Box>
      )}
      {showNoResultsMessage ? (
        <StyledRoverLoading isLoading={resultsLoading} centerContent>
          <NoResults />
        </StyledRoverLoading>
      ) : (
        <>
          {!shouldDisplayMarketingContextHeader && (
            <ResultsInfoBanner onFaqPreviewBannerReadMore={onFaqPreviewBannerReadMore} />
          )}
          <StyledRoverLoading isLoading={resultsLoading}>{trainerCards}</StyledRoverLoading>
        </>
      )}
    </SearchResultSectionLayout>
  );
}
